/**
 * Company Create Page
 * Page for creating new companies
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
} from '@mui/material';
import { Business as BusinessIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { CompanyForm } from './components';
import { PageLayout, PageBreadcrumb } from '../../components';

// Services and types
import { companyService } from '../../services/companyService';
// Types
import { CompanyFormData, CreateCompanyRequest, ModuleType, CompanyType, CompanyStatus, ContactType, DocumentType, ESocialSendMode } from '../../types/company';

const CompanyCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  // Handle form submission
  const handleSubmit = async (data: CompanyFormData) => {
    setLoading(true);
    
    try {
      console.log('🏢 Creating company:', data);
      
      // CompanyForm already converts modules to ModuleType[]
      const modulesArray = data.modules as unknown as ModuleType[];
      
      const requestData: CreateCompanyRequest = {
        legalName: data.legalName,
        tradeName: data.tradeName ?? undefined,
        cnpj: data.cnpj,
        zipCode: data.zipCode,
        state: data.state,
        city: data.city ?? undefined,
        neighborhood: data.neighborhood ?? undefined,
        address: data.address ?? undefined,
        addressNumber: data.addressNumber ?? undefined,
        companyType: data.companyType ? (data.companyType as CompanyType) : undefined,
        status: data.status as CompanyStatus,
        modules: modulesArray,
        // Extended fields
        stateRegistration: data.stateRegistration || undefined,
        municipalRegistration: data.municipalRegistration || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        whatsapp: data.whatsapp || undefined,
        ibgeCode: data.ibgeCode || undefined,
        documentType: data.documentType ? (data.documentType as DocumentType) : undefined,
        documentNumber: data.documentNumber || undefined,
        ecacExpirationDate: data.ecacExpirationDate || undefined,
        esocialSendMode: data.esocialSendMode ? (data.esocialSendMode as ESocialSendMode) : undefined,
        conditionStartDate: data.conditionStartDate || undefined,
        parentCompanyId: data.parentCompanyId ? Number(data.parentCompanyId) : undefined,
      };

      const company = await companyService.createCompany(requestData);

      // Save contacts if available
      const contacts = (data as any).contacts as Array<{ contactType: string; name: string; email?: string; phone?: string }> | undefined;
      if (contacts && contacts.length > 0 && company.id) {
        for (const contact of contacts) {
          try {
            await companyService.createContact(String(company.id), {
              contactType: contact.contactType as ContactType,
              name: contact.name,
              email: contact.email,
              phone: contact.phone,
              isActive: true,
            });
          } catch (err) {
            console.warn('Contact save failed:', err);
          }
        }
      }

      // Save CNAEs from CNPJ lookup if available
      const cnaes = (data as any).cnaes as Array<{ cnaeCode: string; description: string; isPrimary: boolean }> | undefined;
      if (cnaes && cnaes.length > 0 && company.id) {
        try {
          await companyService.bulkCreateCnaes(String(company.id), cnaes);
        } catch (cnaeError) {
          console.warn('CNAEs could not be saved:', cnaeError);
        }
      }

      enqueueSnackbar('Empresa criada com sucesso!', { variant: 'success' });
      navigate('/companies');
    } catch (error: any) {
      console.error('❌ Error creating company:', error);
      const errorMessage = (error.response?.data?.message || error.message) ?? 'Erro ao criar empresa';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/companies');
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <PageBreadcrumb
            items={[
              { label: 'Início', path: '/' },
              { label: 'Empresas', path: '/companies' },
              { label: 'Nova Empresa' },
            ]}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h4" component="h1">
              Nova Empresa
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Cadastre uma nova empresa no sistema
          </Typography>
        </Box>

        {/* Form Card */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <CompanyForm
              mode="create"
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default CompanyCreatePage;